import React, { useEffect, useState } from "react";
import { Box, Button, Select } from "@chakra-ui/react";
import { Tooltip } from "@mui/material";
import InputField from "components/fields/InputField";
import BatchSearchSubaccountListModal from "../../batch/components/BatchSearchSubaccountListModal";

export type SearchField = { key: string; label: string };
export type SearchFieldsMap = Record<string, SearchField[]>;

export type Filters = {
  asset: string;
  key: string;
  value: string;
};

const ASSET_OPTIONS = [
  "AUD",
  "EUR",
  "GBP",
  "CAD",
  "USD",
  "NZD",
  "BTC",
  "USDT",
];

interface Props {
  isLoading: boolean;
  isFilterLoading: boolean;
  searchFields: SearchFieldsMap;
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  selectedSubaccount: any;
  setSelectedSubaccount: React.Dispatch<any>;
  handleSearchSubmit: () => void;
  handleSearchClear: () => void;
}

const ReceiverAccountSearchForm: React.FC<Props> = ({
  isLoading,
  isFilterLoading,
  searchFields,
  filters,
  setFilters,
  selectedSubaccount,
  setSelectedSubaccount,
  handleSearchSubmit,
  handleSearchClear,
}) => {
  const [isSubaccountModalOpen, setIsSubaccountModalOpen] = useState(false);

  const fieldsForAsset = filters.asset ? searchFields[filters.asset] || [] : [];

  // If the asset changes and only one field is available, auto-select it.
  useEffect(() => {
    if (filters.asset && fieldsForAsset.length === 1) {
      setFilters((prev) =>
        prev.key === fieldsForAsset[0].key
          ? prev
          : { ...prev, key: fieldsForAsset[0].key }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.asset, fieldsForAsset.length]);

  const handleAssetChange = (e: any) => {
    const nextAsset = e.target.value;
    setFilters({ asset: nextAsset, key: "", value: "" });
  };

  const handleFieldChange = (e: any) => {
    setFilters((prev) => ({ ...prev, key: e.target.value, value: "" }));
  };

  const handleValueChange = (e: any) => {
    setFilters((prev) => ({ ...prev, value: e.target.value }));
  };

  const handleSubaccountSelect = (subaccountData: any) => {
    setSelectedSubaccount(subaccountData);
  };

  const handleClearSubaccount = () => {
    setSelectedSubaccount(null);
  };

  const handleClear = () => {
    setSelectedSubaccount(null);
    handleSearchClear();
  };

  const trimmedValue = filters.value.trim();
  const canSubmit =
    !isLoading &&
    !isFilterLoading &&
    !!selectedSubaccount?.id &&
    !!filters.asset &&
    !!filters.key &&
    trimmedValue.length >= 3;

  const disableSecondaryFields = !filters.asset || isFilterLoading;
  const disableValueInput = !filters.key || isFilterLoading;

  return (
    <Box className="mb-4 mt-2 rounded-lg bg-white p-6 shadow-lg ring-1 ring-gray-100">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        {/* Subaccount */}
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Subaccount <span className="text-red-500">*</span>
          </label>
          <Tooltip
            title={
              selectedSubaccount ? selectedSubaccount.suba_name : "Select Subaccount"
            }
            arrow
            placement="top"
          >
            <div
              onClick={() =>
                !isFilterLoading && !isLoading && setIsSubaccountModalOpen(true)
              }
              className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                isFilterLoading || isLoading
                  ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                  : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
              }`}
            >
              <span className="truncate">
                {selectedSubaccount
                  ? selectedSubaccount.suba_name
                  : "Select Subaccount"}
              </span>
              {selectedSubaccount && !isFilterLoading && !isLoading && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearSubaccount();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                >
                  &times;
                </button>
              )}
            </div>
          </Tooltip>
        </div>

        {/* Asset */}
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Asset <span className="text-red-500">*</span>
          </label>
          <Select
            isDisabled={isLoading || isFilterLoading}
            id="asset"
            value={filters.asset}
            onChange={handleAssetChange}
            className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
          >
            <option value="">
              {isFilterLoading ? "Retrieving..." : "Select Asset"}
            </option>
            {!isFilterLoading &&
              ASSET_OPTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
          </Select>
        </div>

        {/* Field */}
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Field <span className="text-red-500">*</span>
          </label>
          <Select
            isDisabled={isLoading || disableSecondaryFields || fieldsForAsset.length === 0}
            id="key"
            value={filters.key}
            onChange={handleFieldChange}
            className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
          >
            <option value="">
              {filters.asset ? "Select Field" : "Please select asset"}
            </option>
            {fieldsForAsset.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </Select>
        </div>

        {/* Keyword */}
        <div className="tracking-tight text-gray-900">
          <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
            Keyword <span className="text-red-500">*</span>
            <span className="ml-2 text-xs font-normal text-gray-500">
              (min 3 chars)
            </span>
          </label>
          <InputField
            disabled={isLoading || disableValueInput}
            variant="auth"
            extra="shadow-sm rounded-md"
            label=""
            placeholder={
              filters.key
                ? "Enter keyword"
                : filters.asset
                ? "Choose a field"
                : "Please select asset"
            }
            id="value"
            type="text"
            autoComplete="off"
            value={filters.value}
            onChange={handleValueChange}
          />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-end space-x-4">
        <Button
          isDisabled={isLoading}
          onClick={handleClear}
          className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
        >
          Clear
        </Button>

        <Button
          isDisabled={!canSubmit}
          isLoading={isLoading}
          loadingText="Searching..."
          onClick={handleSearchSubmit}
          className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="relative z-10 tracking-tight">
            Search Receiver Accounts
          </span>
        </Button>
      </div>

      {isSubaccountModalOpen && (
        <BatchSearchSubaccountListModal
          isOpen={isSubaccountModalOpen}
          onClose={() => setIsSubaccountModalOpen(false)}
          onSelect={handleSubaccountSelect}
        />
      )}
    </Box>
  );
};

export default ReceiverAccountSearchForm;
